// ===== main.js - NOUVELLE ARCHITECTURE =====

import { WelcomeScreen } from "./src/components/WelcomeScreen/WelcomeScreen.js";
import { ProjectCreator } from "./src/components/ProjectCreator/ProjectCreator.js";
import { TiroirNavigation } from "./src/components/TiroirNavigation/TiroirNavigation.js";
import { ModulesView } from "./src/components/ModulesView/ModulesView.js";
import { ConfigurationRibbon } from "./src/components/ConfigurationRibbon/ConfigurationRibbon.js";
import { APP_STATES, CUSTOM_EVENTS, createDefaultProject, validateProjectConfig } from "./src/utils/constants.js";
import {
  loadCurrentProject,
  saveProject,
  exportProjectToJSON,
  importProjectFromJSON,
  toggleTiroir,
  toggleModule,
  clearAllData,
} from "./src/utils/storage.js";
/**
 * Récupérer la version de l'application de manière robuste
 */
function getAppVersion() {
  // 1. Utiliser la variable injectée par Vite (priorité haute)
  if (typeof __APP_VERSION__ !== 'undefined') {
    return __APP_VERSION__;
  }
  
  // 2. Fallback pour les variables d'environnement Vite
  if (import.meta.env.VITE_APP_VERSION) {
    return import.meta.env.VITE_APP_VERSION;
  }
  
  // 3. Fallback par défaut
  return '1.0.0';
}

/**
 * Formater la version pour l'affichage
 */
function formatVersion(version) {
  return version.startsWith('V') ? version : `V${version}`;
}

// Variable globale pour la version
let swVersion = formatVersion(getAppVersion());

/**
 * Récupère la version formatée
 */
export function getSwVersion() {
  return swVersion;
}

/**
 * Classe principale de l'application avec gestion de projets
 */
export class FiberConfigApp {
  constructor() {
    this.currentState = APP_STATES.LOADING;
    this.currentProject = null;
    this.appContainer = document.getElementById("app");
    
    // Récupérer la version via la nouvelle méthode
    this.version = getAppVersion();

    // Composants
    this.components = {
      welcomeScreen: null,
      projectCreator: null,
      navigation: null,
      modulesView: null,
      ConfigurationRibbon: null,
    };

    this.init();
  }

  async init() {
    try {
      // Nettoyer le message de chargement
      this.appContainer.innerHTML = "";

      // Charger le CSS principal
      this.loadMainStyles();

      // Configurer les événements globaux
      this.setupGlobalEvents();

      // Vérifier s'il y a un projet existant
      const existingProject = loadCurrentProject();

      if (existingProject) {
        this.currentProject = existingProject;
        this.showMainApp();
      } else {
        this.showWelcomeScreen();
      }
    } catch (error) {
      console.error("❌ Erreur lors de l'initialisation:", error);
      this.showError("Erreur lors du chargement de l'application");
    }
  }

  /**
   * Charger les styles principaux
   */
  loadMainStyles() {
    const styleFiles = [
      "./src/styles/welcome-screen.css",
      "./src/styles/project-creator.css",
      "./src/styles/navigation.css",
    ];

    styleFiles.forEach((file) => {
      if (!document.querySelector(`link[href="${file}"]`)) {
        const link = document.createElement("link");
        link.rel = "stylesheet";
        link.href = file;
        document.head.appendChild(link);
      }
    });
  }

  /**
   * Afficher l'écran d'accueil
   */
  showWelcomeScreen() {
    this.currentState = APP_STATES.WELCOME;
    this.hideAllComponents();

    this.components.welcomeScreen = new WelcomeScreen(this.appContainer, {
      onCreateProject: () => this.showProjectCreator(),
      onLoadProject: () => this.handleLoadProject(),
    });

    this.components.welcomeScreen.animate();
  }

  /**
   * Afficher le créateur de projet
   */
  showProjectCreator() {
    this.currentState = APP_STATES.PROJECT_CREATOR;
    this.hideAllComponents();

    this.components.projectCreator = new ProjectCreator(this.appContainer, {
      onProjectCreate: (config) => this.handleProjectCreate(config),
      onCancel: () => this.showWelcomeScreen(),
    });

    this.components.projectCreator.animate();
  }

  /**
   * Afficher l'application principale
   */
  showMainApp() {
    this.currentState = APP_STATES.MAIN_APP;
    this.hideAllComponents();

    if (!this.currentProject) {
      console.error("❌ Aucun projet chargé pour l'app principale");
      this.showWelcomeScreen();
      return;
    }

    // Créer la structure de l'app principale
    this.appContainer.innerHTML = `
      <div class="main-app">
        <div class="main-app-header">
          <h1 class="main-app-title">Configuration des Fibres Optiques</h1>
          <div class="main-app-description-container">
            <p class="main-app-description">
              Interface de configuration des tiroirs de fibres optiques. 
              Sélectionnez un tiroir dans la liste ci-dessous pour accéder à ses modules, 
              puis configurez l'état de chaque fibre manuellement ou en utilisant la reconnaissance vocale.
            </p>
          </div>
        </div>
        <div class="app-navigation" id="appNavigation"></div>
        <div class="app-content" id="appContent"></div>
      </div>
    `;

    // ✅ CORRECTION : Définir navigationContainer
    const navigationContainer = document.getElementById("appNavigation");
    this.components.navigation = new TiroirNavigation(navigationContainer, {
      activeTiroirs: this.currentProject.activeTiroirs,
      onTiroirSelect: (tiroirNum) => this.handleTiroirSelect(tiroirNum),
      onExportJSON: () => this.handleQuickExport(), // ← Nouveau
      onAbandon: () => this.handleClearAllData(), // ← Nouveau
    });

    // ✅ CORRECTION : Définir contentContainer
    const contentContainer = document.getElementById("appContent");
    this.components.modulesView = new ModulesView(contentContainer, {
      project: this.currentProject,
      currentTiroir: this.currentProject.activeTiroirs[0] || 1,
      onModuleEdit: (moduleTitle, moduleLetter) => this.handleModuleEdit(moduleTitle, moduleLetter),
      onModuleToggle: (tiroirNum, moduleLetter, isActive) => this.handleModuleToggle(tiroirNum, moduleLetter, isActive),
    });

    // Initialiser le ruban de configuration
    this.components.ConfigurationRibbon = new ConfigurationRibbon(document.body);

    // Mettre à jour les statistiques initiales
    this.updateNavigationStats();

    // Animer l'entrée
    this.components.navigation.animate();
    this.components.modulesView.animate();
  }

  /**
   * Gérer la création d'un projet
   */
  handleProjectCreate(projectConfig) {
    try {
      // Valider la configuration
      const validation = validateProjectConfig(projectConfig);
      if (!validation.isValid) {
        alert("Configuration invalide: " + validation.errors.join(", "));
        return;
      }

      // Créer le projet
      const project = {
        ...createDefaultProject(),
        name: projectConfig.name,
        fiberType: projectConfig.fiberType,
        activeTiroirs: [...projectConfig.activeTiroirs],
      };

      // Générer les modules actifs selon le type de fibre
      const moduleLetters =
        projectConfig.fiberType === 24
          ? ["A", "B", "C", "D", "E", "F"]
          : ["A", "B", "C", "D", "E", "F", "G", "H", "I", "J", "K", "L"];

      project.activeModules = {};
      projectConfig.activeTiroirs.forEach((tiroirNum) => {
        project.activeModules[`T${tiroirNum}`] = [...moduleLetters];
      });

      // Sauvegarder le projet
      const success = saveProject(project);
      if (!success) {
        alert("Erreur lors de la sauvegarde du projet");
        return;
      }

      // Charger le projet créé
      this.currentProject = project;

      // Déclencher événement
      window.dispatchEvent(
        new CustomEvent(CUSTOM_EVENTS.PROJECT_CREATED, {
          detail: { project },
        })
      );

      // Afficher l'app principale
      this.showMainApp();
    } catch (error) {
      console.error("❌ Erreur création projet:", error);
      alert("Erreur lors de la création du projet: " + error.message);
    }
  }

  /**
   * Gérer le chargement d'un projet
   */
  handleLoadProject() {
    try {
      // Créer un input file pour sélectionner le JSON
      const input = document.createElement("input");
      input.type = "file";
      input.accept = ".json";

      input.addEventListener("change", (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = (event) => {
          try {
            const jsonString = event.target.result;
            const result = importProjectFromJSON(jsonString);

            if (result.success) {
              this.currentProject = result.project;

              // Déclencher événement
              window.dispatchEvent(
                new CustomEvent(CUSTOM_EVENTS.PROJECT_LOADED, {
                  detail: { project: result.project },
                })
              );

              // Afficher l'app principale
              this.showMainApp();
            } else {
              alert("Erreur lors du chargement: " + result.error);
            }
          } catch (error) {
            console.error("❌ Erreur lecture fichier:", error);
            alert("Erreur lors de la lecture du fichier");
          }
        };

        reader.readAsText(file);
      });

      input.click();
    } catch (error) {
      console.error("❌ Erreur chargement projet:", error);
      alert("Erreur lors du chargement du projet");
    }
  }

  /**
   * Gérer la sélection d'un tiroir
   */
  handleTiroirSelect(tiroirNum) {
    if (this.components.modulesView) {
      this.components.modulesView.setCurrentTiroir(tiroirNum);
    }
  }

  /**
   * Gérer l'édition d'un module
   */
  handleModuleEdit(moduleTitle, moduleLetter) {
    // Déclencher l'ouverture du ruban
    window.dispatchEvent(
      new CustomEvent(CUSTOM_EVENTS.MODULE_EDIT_REQUESTED, {
        detail: { moduleTitle, moduleLetter },
      })
    );
  }

  /**
   * Gérer le toggle d'un module
   */
  handleModuleToggle(tiroirNum, moduleLetter, isActive) {
    try {
      const success = toggleModule(tiroirNum, moduleLetter, isActive);
      if (success) {
        // Recharger le projet
        this.currentProject = loadCurrentProject();

        // Mettre à jour la vue des modules
        if (this.components.modulesView) {
          this.components.modulesView.updateProject(this.currentProject);
          this.components.modulesView.refresh();
        }

        // Déclencher événement
        window.dispatchEvent(
          new CustomEvent(CUSTOM_EVENTS.MODULE_TOGGLED, {
            detail: { tiroirNum, moduleLetter, isActive, project: this.currentProject },
          })
        );
      } else {
        alert("Erreur lors de la modification du module");
      }
    } catch (error) {
      console.error("❌ Erreur toggle module:", error);
      alert("Erreur lors de la modification du module");
    }
  }

  /**
   * Mettre à jour les statistiques de navigation
   */
  updateNavigationStats() {
    if (!this.components.navigation || !this.currentProject) return;

    this.currentProject.activeTiroirs.forEach((tiroirNum) => {
      // Calculer les stats pour ce tiroir
      // Cette logique sera étendue quand les données de fibres seront intégrées
      const stats = { percentage: 0 }; // Placeholder
      this.components.navigation.updateTiroirStats(tiroirNum, stats);
    });
  }

  /**
   * Masquer tous les composants
   */
  hideAllComponents() {
    Object.values(this.components).forEach((component) => {
      if (component && typeof component.hide === "function") {
        component.hide();
      } else if (component && typeof component.unmount === "function") {
        component.unmount();
      }
    });

    // Nettoyer les références des composants
    this.components = {
      welcomeScreen: null,
      projectCreator: null,
      navigation: null,
      modulesView: null,
      ConfigurationRibbon: this.components.ConfigurationRibbon, // Garder le ruban
    };
  }

  /**
   * Configurer les événements globaux
   */
  setupGlobalEvents() {
    // Gestion d'erreur globale
    window.addEventListener("error", (event) => {
      console.error("❌ Erreur globale capturée:", event.error);
    });

    // Gestion des erreurs de promesses non catchées
    window.addEventListener("unhandledrejection", (event) => {
      console.error("❌ Promise rejetée non gérée:", event.reason);
    });

    // Événements de changement de données
    window.addEventListener(CUSTOM_EVENTS.DATA_CHANGED, (event) => {
      this.handleDataChanged(event.detail);
    });
  }

  /**
   * Gérer les changements de données
   */
  handleDataChanged(detail) {
    // Recharger le projet pour s'assurer d'avoir les dernières données
    this.currentProject = loadCurrentProject();

    // Rafraîchir les composants
    if (this.components.modulesView) {
      this.components.modulesView.refresh();
    }

    this.updateNavigationStats();
  }

  /**
   * Afficher une erreur
   */
  showError(message) {
    const errorDiv = document.createElement("div");
    errorDiv.className = "app-error";
    errorDiv.innerHTML = `
      <h3>❌ Erreur</h3>
      <p>${message}</p>
      <button class="app-error-reload">🔄 Recharger la page</button>
    `;

    const reloadBtn = errorDiv.querySelector(".app-error-reload");
    reloadBtn.addEventListener("click", () => window.location.reload());

    this.appContainer.insertBefore(errorDiv, this.appContainer.firstChild);
  }

  /**
   * Obtenir les informations de l'application
   */
  getInfo() {
    return {
      version: this.version,
      currentState: this.currentState,
      currentProject: this.currentProject
        ? {
            name: this.currentProject.name,
            fiberType: this.currentProject.fiberType,
            activeTiroirs: this.currentProject.activeTiroirs,
            createdAt: this.currentProject.createdAt,
          }
        : null,
      components: Object.fromEntries(
        Object.entries(this.components).map(([key, component]) => [key, component ? "loaded" : "null"])
      ),
    };
  }

  /**
   * Nettoyer les ressources
   */
  destroy() {
    // Nettoyer tous les composants
    Object.values(this.components).forEach((component) => {
      if (component && typeof component.destroy === "function") {
        component.destroy();
      }
    });
  }

  // À ajouter dans votre main.js réel, dans la classe FiberConfigApp

  /**
   * Export rapide JSON sans prompt
   */
  handleQuickExport() {
    if (!this.currentProject) {
      alert("Aucun projet à exporter");
      return;
    }

    try {
      console.log("******************************");
      console.log("handleQuickExport =", this.currentProject);

      const result = exportProjectToJSON(this.currentProject);

      if (result.success) {
      } else {
        console.error("❌ Erreur lors de l'export:", result.error);
        alert("Erreur lors de l'export: " + result.error);
      }
    } catch (error) {
      console.error("❌ Erreur export:", error);
      alert("Erreur lors de l'export du projet: " + error.message);
    }
  }

  /**
   * Supprimer tout le storage et retour accueil
   */
  /**
   * Supprimer tout le storage et retour accueil
   */
  handleClearAllData() {
    try {
      const success = clearAllData();

      if (success) {
        // ✅ AJOUTER : Nettoyer complètement l'interface
        this.currentProject = null;

        // ✅ AJOUTER : Vider complètement le container
        this.appContainer.innerHTML = "";

        // ✅ AJOUTER : Reset de l'état de l'application
        this.currentState = APP_STATES.WELCOME;

        this.hideAllComponents();
        this.showWelcomeScreen();
      } else {
        console.error("❌ Erreur lors de la suppression");
        alert("Erreur lors de la suppression des données");
      }
    } catch (error) {
      console.error("❌ Erreur suppression:", error);
      alert("Erreur lors de la suppression des données: " + error.message);
    }
  }
}

/**
 * Initialiser l'application et exposer globalement
 */
export async function initializeApp() {
  // Initialiser l'application
  const app = new FiberConfigApp();

  // Exposer globalement pour debug
  window.app = app;

  // Debug global
  window.exportProject = () => {
    if (window.app && window.app.currentProject) {
      window.app.handleQuickExport();
    } else {
    }
  };

  window.getAppInfo = () => {
    return window.app ? window.app.getInfo() : null;
  };

  return app;
}